'use client';

import { formatDistanceToNow } from 'date-fns';
import { ChevronDown, ChevronUp, Edit2, MoreHorizontal, Pin, Reply, Trash2 } from 'lucide-react';
import { useGetReplies } from '../../hooks/useComments';
import { cn } from '../../lib/utils';
import type { Comment, User } from '../../types/comments';
import { Button } from '../../ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../../ui/dropdown-menu';
import { UserAvatar } from '../../ui/user-avatar';
import { RichTextContent } from '../rich-text-content';
import { CommentAttachments } from './comment-attachments';
import { ReactionDisplay } from './reaction-display';
import { ReactionPicker } from './reaction-picker';

interface CommentCardProps {
  comment: Comment;
  currentUser: User;
  users: User[];
  variant?: 'sidebar' | 'overlay';
  /** When true, renders in compact reply mode (no pin, no nested reply toggle) */
  isReply?: boolean;
  onReaction?: (commentId: number, emoji: string) => void;
  onDelete?: (commentId: number) => void;
  onEdit?: (comment: Comment) => void;
  onPin?: (commentId: number) => void;
  onReply?: (comment: Comment) => void;
  /** Controls whether the replies section is expanded (controlled externally for top-level cards) */
  repliesOpen?: boolean;
  onToggleReplies?: () => void;
  isReadOnly?: boolean;
}

export function CommentCard({
  comment,
  currentUser,
  users,
  variant = 'sidebar',
  isReply = false,
  onReaction,
  onDelete,
  onEdit,
  onPin,
  onReply,
  repliesOpen,
  onToggleReplies,
  isReadOnly = false,
}: CommentCardProps) {
  const isOverlay = variant === 'overlay';

  // Only top-level comments fetch reply counts
  const { data: replies = [] } = useGetReplies(isReply ? null : comment.id);
  const replyCount = isReply ? 0 : replies.length;

  return (
    <div
      data-id="comment-card"
      className={cn(
        'group flex gap-3 py-2 px-2 transition-colors relative rounded-md',
        isOverlay ? 'hover:bg-white/5' : 'hover:bg-muted/50',
        comment.isPinned && (isOverlay ? 'bg-amber-500/10' : 'bg-amber-500/5')
      )}
    >
      <UserAvatar user={comment.author} size={isReply ? 'sm' : 'md'} />

      <div data-id="comment-content" className="min-w-0 flex-1 pr-0">
        <div className="flex items-center gap-2">
          <span className={cn('font-semibold text-sm', isOverlay && 'text-white')}>
            {comment.author.fullName}
          </span>
          <span className="text-[10px] text-muted-foreground opacity-70">
            {formatDistanceToNow(new Date(comment.createdAt * 1000), { addSuffix: true })}
          </span>
          {comment.isPinned && (
            <div className="flex items-center gap-1 text-[9px] font-bold text-amber-500 uppercase tracking-widest">
              <Pin className="size-2.5 fill-current" />
              Pinned
            </div>
          )}
        </div>

        <RichTextContent
          className={cn(
            'prose prose-sm mt-0.5 max-w-none break-words',
            isOverlay ? 'text-gray-200' : 'text-foreground'
          )}
          html={comment.content}
        />

        {comment.hasAttachments && <CommentAttachments commentId={comment.id} />}

        <div className="mt-1.5 flex items-center gap-2 flex-wrap">
          <ReactionDisplay
            reactions={comment.reactions}
            currentUserId={currentUser.id}
            users={users}
            onReaction={(emoji) => !isReadOnly && onReaction?.(comment.id, emoji)}
          />

          {/* Reply toggle — only for top-level comments */}
          {!isReply && replyCount > 0 && onToggleReplies && (
            <button
              onClick={onToggleReplies}
              className={cn(
                'flex items-center gap-1 text-[11px] font-medium transition-colors',
                isOverlay
                  ? 'text-white/60 hover:text-white/90'
                  : 'text-muted-foreground hover:text-foreground'
              )}
            >
              {repliesOpen ? <ChevronUp className="size-3" /> : <ChevronDown className="size-3" />}
              {replyCount} {replyCount === 1 ? 'reply' : 'replies'}
            </button>
          )}
        </div>
      </div>

      {/* Hover actions */}
      {!isReadOnly && (
        <div
          data-id="hover-options"
          className="absolute top-2 right-2 flex items-start gap-1 opacity-0 transition-opacity group-hover:opacity-100"
        >
          <ReactionPicker onSelect={(emoji) => onReaction?.(comment.id, emoji)} />

          {/* Reply button — only for top-level, non-overlay */}
          {!isReply && onReply && (
            <Button
              variant="ghost"
              size="icon-sm"
              className={cn(
                'size-7 text-muted-foreground hover:text-foreground',
                isOverlay && 'hover:bg-white/10 hover:text-white'
              )}
              onClick={() => onReply(comment)}
              title="Reply"
            >
              <Reply className="size-3.5" />
              <span className="sr-only">Reply</span>
            </Button>
          )}

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                size="icon-sm"
                className={cn(
                  'size-7 text-muted-foreground hover:text-foreground',
                  isOverlay && 'hover:bg-white/10 hover:text-white'
                )}
              >
                <MoreHorizontal className="size-4" />
                <span className="sr-only">More options</span>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-40">
              {onEdit && currentUser.id === comment.author.id && (
                <DropdownMenuItem onClick={() => onEdit(comment)}>
                  <Edit2 className="size-4 mr-2" />
                  Edit
                </DropdownMenuItem>
              )}
              {!isReply && onPin && (
                <DropdownMenuItem onClick={() => onPin(comment.id)}>
                  <Pin className={cn('size-4 mr-2', comment.isPinned && 'fill-current')} />
                  {comment.isPinned ? 'Unpin' : 'Pin'}
                </DropdownMenuItem>
              )}
              {currentUser.id === comment.author.id && (
                <DropdownMenuItem
                  variant="destructive"
                  onClick={() => onDelete?.(comment.id)}
                  className="text-destructive"
                >
                  <Trash2 className="size-4 mr-2" />
                  Delete
                </DropdownMenuItem>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      )}
    </div>
  );
}
